<script setup>
/**
 * XN-4537 — the policy line editor.
 *
 * Three fields and a preview. There is no description, no list of what is
 * covered and no amounts, and that is the design rather than a first cut:
 * describing what a policy covers is a statement about somebody's contract, and
 * a text box on a settings page is the wrong place for an agency owner to
 * author one.
 *
 * The header states the stake because the name is not a label on this page — it
 * is the option on the registration form, the chip on every record and the
 * wording of every segment built on the line. Renaming reaches all of them, so
 * the save says how far it reached before it happens, not after.
 */
import { computed, ref, watch } from 'vue';
import { plural } from '../../helpers/plural';
import { useUnsavedChanges } from '../../composables/useUnsavedChanges';

import PolicyLineCardPreview from './PolicyLineCardPreview.vue';
import PolicyLineIcon from './PolicyLineIcon.vue';
import IconBack from '../icons/IconBack.vue';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlField from '../xl/XlField.vue';
import XlModal from '../xl/XlModal.vue';
import {
  COLORS,
  ICONS,
  MAX_NAME_LENGTH,
  UNTITLED,
  canonical,
  isDuplicateName,
  validateName,
} from '../../helpers/policyLines.js';

const props = defineProps({
  line: { type: Object, required: true },
  /** Every other line in the hub, for the uniqueness and look-alike checks. */
  lines: { type: Array, default: () => [] },
  holderCount: { type: Number, default: null },
  isSaving: { type: Boolean, default: false },
  // Archiving is the owner's alone (the server refuses anybody else).
  canArchive: { type: Boolean, default: true },
});

const emit = defineEmits(['save', 'archive', 'close']);

const draft = ref(blank());
const error = ref('');
const confirmingRename = ref(false);

function blank() {
  return {
    name: props.line.name ?? '',
    icon: props.line.icon ?? 'shield',
    color: props.line.color_hex ?? COLORS[0],
  };
}

watch(() => props.line, () => {
  draft.value = blank();
  error.value = '';
});

const heading = computed(() => draft.value.name.trim() || UNTITLED);

/**
 * "Nobody holds this yet" rather than "0 policyholders": a count of nothing
 * reads as a measurement, and this line is telling the owner the change is free.
 */
const stake = computed(() => (props.holderCount
  ? plural(props.holderCount, 'policyholder holds this. The name is what they see.', 'policyholders hold this. The name is what all of them see.')
  : 'Nobody holds this yet.'));

/**
 * A line that exists on the server. A new one is held in memory until Save, so
 * an id is exactly the question "is there anything here to archive yet" — and
 * every line that HAS been saved keeps the option, named or not.
 */
const isSaved = computed(() => Boolean(props.line.id) && !props.line.archived);

const nameChanged = computed(() => canonical(draft.value.name) !== canonical(props.line.name));

const hasChanges = computed(() => nameChanged.value
  || draft.value.icon !== props.line.icon
  || draft.value.color !== props.line.color_hex);

/**
 * Allowed, warned. Two lines may share a look — but the card is how a
 * policyholder tells one policy from another at a glance, so it is worth
 * saying that out loud rather than blocking it.
 */
const lookAlike = computed(() => props.lines.find((other) => other.id !== props.line.id
  && !other.archived
  && other.icon === draft.value.icon
  && other.color_hex === draft.value.color));

/** What a rename is about to reword, named before the save rather than after. */
const renameReach = computed(() => [
  'the policy-type question on your registration form',
  props.holderCount
    ? `the tag on ${plural(props.holderCount, "policyholder's record", "policyholders' records")}`
    : 'the tag on every policyholder record',
  'every segment in Communications built on this line',
]);

const validate = () => {
  // Empty is allowed — that is a draft, and it saves. It just is not offered on
  // the registration form until it has a name.
  const message = validateName(draft.value.name, { allowEmpty: true });

  if (message) return message;

  if (isDuplicateName(draft.value.name, props.lines, props.line.id)) {
    return 'Another policy line already uses this name. Segments and the registration form need them to be different.';
  }

  return '';
};

const attemptSave = () => {
  error.value = validate();
  if (error.value) return;

  // A brand-new line has nothing pointing at it yet, so there is nothing to
  // warn about; the warning is for a rename that moves existing wording.
  if (nameChanged.value && props.line.name) {
    confirmingRename.value = true;
    return;
  }

  commit();
};

const commit = () => {
  confirmingRename.value = false;
  emit('save', { ...draft.value, name: canonical(draft.value.name) });
};

/**
 * The page's `onBeforeRouteLeave` only fires on actual router navigation, so
 * this button walked straight past the unsaved-changes promise and discarded
 * the edit. Same question, asked here.
 */
const attemptClose = () => {
  if (hasChanges.value
    && !window.confirm('You have unsaved changes to this policy line. Leave without saving?')) {
    return;
  }

  emit('close');
};

// Leaving the page with a change asks first (App.vue), as every configuration
// screen does; the sidebar's own item closes the editor back to the list.
useUnsavedChanges({ key: 'policy-line', dirty: hasChanges, onSectionHome: () => emit('close') });

defineExpose({ hasChanges });
</script>

<template>
  <div class="line-editor">
    <XlButton variant="ghost" size="sm" class="line-editor__back" @click="attemptClose">
      <IconBack /> All policy lines
    </XlButton>

    <header class="line-editor__head">
      <div class="line-editor__heading">
        <h1>{{ heading }}</h1>
        <p v-if="props.holderCount !== null">{{ stake }}</p>
      </div>
      <XlButton :disabled="!hasChanges" :loading="props.isSaving" @click="attemptSave">Save</XlButton>
    </header>

    <div class="line-editor__cols">
      <XlCard padded class="line-editor__card">
        <!--
          One flush column holding both fields. The Illustration block used to
          be a sibling with its own margin, which left it a few pixels off the
          left edge the Name field established; sharing a column with no
          horizontal padding anywhere below makes that misalignment impossible
          rather than corrected.
        -->
        <XlField
          v-model="draft.name"
          name="policy_line_name"
          label="Name"
          placeholder="e.g. Home insurance"
          :maxlength="MAX_NAME_LENGTH"
          :error="error"
          hint="Policyholders see this on their policy card, and it is the wording used on the registration form and in segment names."
        />

        <div class="line-editor__identity">
          <span class="line-editor__label">Illustration and color</span>

          <div class="line-editor__icons" role="radiogroup" aria-label="Illustration">
            <button
              v-for="icon in ICONS"
              :key="icon"
              type="button"
              role="radio"
              :aria-checked="draft.icon === icon"
              :aria-label="icon"
              class="line-editor__icon"
              :class="{ 'line-editor__icon--on': draft.icon === icon }"
              :style="draft.icon === icon
                ? { borderColor: draft.color, background: `${draft.color}16`, color: draft.color }
                : null"
              @click="draft.icon = icon"
            ><PolicyLineIcon :name="icon" /></button>
          </div>

          <div class="line-editor__colors" role="radiogroup" aria-label="Color">
            <button
              v-for="color in COLORS"
              :key="color"
              type="button"
              role="radio"
              :aria-checked="draft.color === color"
              :aria-label="color"
              class="line-editor__color"
              :class="{ 'line-editor__color--on': draft.color === color }"
              :style="{ background: color }"
              @click="draft.color = color"
            />
          </div>

          <div class="line-editor__hints">
            <p class="line-editor__hint">
              How a policyholder tells one policy from another at a glance.
            </p>
            <p class="line-editor__hint">
              Two lines can share a look, but it is worth not letting them.
            </p>
          </div>

          <p v-if="lookAlike" class="line-editor__warn" data-testid="look-alike">
            {{ lookAlike.name || UNTITLED }} already uses this illustration and color.
          </p>
        </div>
      </XlCard>

      <XlCard padded class="line-editor__preview">
        <!-- A heading and its own subtitle are one block, so the column's gap
             falls between them and the card, not between the two lines. -->
        <div class="line-editor__preview-head">
          <b>What a policyholder sees</b>
          <p class="line-editor__hint">
            Their own policy number, address and renewal date fill in around this.
          </p>
        </div>

        <PolicyLineCardPreview :name="draft.name" :icon="draft.icon" :color="draft.color" />
      </XlCard>
    </div>

    <!--
      Archive, not Delete. A line somebody holds cannot be removed without
      rewriting their history, so there is no delete anywhere on this page —
      the option that exists is the one that is safe.

      Hidden while a line is still being created, because there is nothing on
      the server to archive yet. Every saved line keeps the option, for
      whoever may archive one: the owner.
    -->
    <footer v-if="isSaved && props.canArchive" class="line-editor__foot" data-testid="line-archive">
      <div>
        <b>Archive this policy line</b>
        <p class="line-editor__hint">
          {{ props.holderCount
            ? 'It stops being offered on the registration form and in the record picker. The policyholders who hold it keep it.'
            : 'It stops being offered on the registration form and in the record picker.' }}
        </p>
      </div>
      <XlButton variant="ghost-danger" size="sm" @click="emit('archive')">Archive</XlButton>
    </footer>

    <XlModal
      v-if="confirmingRename"
      title="Rename this policy line?"
      :subtitle="`“${props.line.name}” becomes “${canonical(draft.name) || UNTITLED}” everywhere it is used.`"
      @close="confirmingRename = false"
    >
      <p class="line-editor__hint">This changes:</p>
      <ul class="line-editor__reach">
        <li v-for="item in renameReach" :key="item">{{ item }}</li>
      </ul>

      <template #actions>
        <XlButton variant="ghost" @click="confirmingRename = false">Cancel</XlButton>
        <XlButton :loading="props.isSaving" @click="commit">Rename</XlButton>
      </template>
    </XlModal>
  </div>
</template>

<style lang="scss" scoped>
.line-editor {
  &__back { margin-bottom: 18px; }

  &__head {
    display: flex;
    align-items: flex-end;
    gap: 14px;
    flex-wrap: wrap;
    margin-bottom: 18px;
  }

  &__heading {
    flex: 1;
    min-width: 220px;

    h1 {
      margin: 0 0 4px;
      font-family: var(--xl-font-family);
      font-size: var(--xl-page-title-size);
      font-weight: 600;
      line-height: var(--xl-page-title-line);
      color: var(--xl-color-primary-text);
    }

    p {
      margin: 0;
      font-size: var(--xl-page-subtitle-size);
      line-height: var(--xl-page-subtitle-line);
      color: var(--xl-color-outline);
    }
  }

  &__cols {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    /* Stretch, not start: the two cards read as one pair, so they share a
       height. That also retires the preview's `position: sticky` — a sticky
       card inside a stretched grid cell has nothing to scroll against, and on
       a page this short it never had. */
    gap: 20px;
  }

  /* Both cards fill the row, and each lays its own content out down the
     full height rather than bunching at the top. */
  &__card, &__preview {
    height: 100%;
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
    gap: 26px;
  }

  &__preview-head {
    display: flex;
    flex-direction: column;
    gap: 5px;

    b { font-size: var(--xl-type-title-small-size, 15px); }
  }

  /* The preview card holds a heading and one card; the card takes the room
     left over so the two sides finish level. */
  &__preview :deep(.policy-card) { margin-top: auto; }

  /*
    The Name field, brought into line with the Illustration block under it.

    `column-reverse` puts the counter — last in the DOM — above the hint, which
    is the order asked for without reversing the markup: the control's
    `aria-describedby` already names both, so a screen reader still gets the
    hint and the count whichever way round they are painted.
  */
  :deep(.xl-field__foot) {
    flex-direction: column-reverse;
    align-items: flex-start;
    gap: 4px;
  }

  :deep(.xl-field__counter) { margin-left: 0; }

  :deep(.xl-field__label) {
    font-weight: 600;
    color: var(--xl-color-primary-text);
  }

  &__hints {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__identity {
    display: flex;
    flex-direction: column;
    /* No horizontal padding or margin on anything in this column, which is
       what keeps every row flush with the Name field above it. */
    gap: 14px;
    padding: 0;
  }

  &__label {
    display: block;
    margin: 0;
    font-size: var(--xl-type-label-large-size, 13px);
    font-weight: 600;
    color: var(--xl-color-primary-text);
    cursor: default;
  }

  &__icons, &__colors {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin: 0;
    padding: 0;
  }

  &__icon {
    box-sizing: border-box;
    width: 46px;
    height: 46px;
    flex: 0 0 46px;
    display: grid;
    place-items: center;
    margin: 0;
    padding: 0;
    border: 1px solid var(--xl-color-border);
    border-radius: var(--xl-radius-sm);
    background: var(--xl-color-surface, #fff);
    color: var(--xl-color-secondary-text);
    cursor: pointer;
  }

  /* Same box as an illustration tile: the two rows are one control in the
     owner's head, and two sizes read as two unrelated pickers. */
  &__color {
    box-sizing: border-box;
    width: 46px;
    height: 46px;
    flex: 0 0 46px;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: var(--xl-radius-sm);
    cursor: pointer;

    &--on {
      outline: 2px solid var(--xl-color-primary-text);
      outline-offset: 2px;
    }
  }

  &__hint {
    margin: 0;
    font-size: var(--xl-type-body-small-size, 12.5px);
    color: var(--xl-color-outline);
  }

  &__warn {
    margin: 0;
    font-size: var(--xl-type-body-small-size, 12.5px);
    color: var(--xl-color-warning);
  }

  &__foot {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    margin-top: 24px;
    padding-top: 18px;
    border-top: 1px solid var(--xl-color-border);

    b { font-size: var(--xl-type-title-small-size, 15px); }
    p { max-width: 60ch; }
  }

  &__reach {
    margin: 8px 0 0;
    padding-left: 20px;
    font-size: var(--xl-type-body-medium-size);
    color: var(--xl-color-secondary-text);

    li { margin: 4px 0; }
  }
}
</style>
